import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { PCMP_MARKETING_DEFAULT_TAB } from '../constants/pcmpMarketing.constants';
import {
  PCMP_MARKETING_BATCHES_TAB,
  PCMP_PROPOSAL_CORRECTION_TAB,
  type PcmpMarketingBatchesParams,
  type PcmpMarketingQueueParams,
  type PcmpMarketingQueueSearch,
  type PcmpMarketingTab,
  type PcmpProposalCorrectionParams,
} from '../types/pcmpMarketing.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const route = getRouteApi('/_auth/pcmp/marketing/');

export function usePcmpMarketingFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpMarketingQueueSearch) => PcmpMarketingQueueSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;

  const tab = search.tab ?? PCMP_MARKETING_DEFAULT_TAB;
  const explained = tab === 'MIS_NOT_VERIFIED' ? search.explained : undefined;

  // Proposal Correction and Batches have their own endpoints; the queue takes the rest.
  const queueParams = useMemo<PcmpMarketingQueueParams | null>(
    () =>
      tab === PCMP_PROPOSAL_CORRECTION_TAB || tab === PCMP_MARKETING_BATCHES_TAB
        ? null
        : { ...appliedFilters, tab, explained, page, limit },
    [appliedFilters, tab, explained, page, limit],
  );

  const batchesParams = useMemo<PcmpMarketingBatchesParams | null>(
    () => (tab === PCMP_MARKETING_BATCHES_TAB ? { ...appliedFilters, page, limit } : null),
    [appliedFilters, tab, page, limit],
  );
  const correctionParams = useMemo<PcmpProposalCorrectionParams | null>(
    () => (tab === PCMP_PROPOSAL_CORRECTION_TAB ? { ...appliedFilters, page, limit } : null),
    [appliedFilters, tab, page, limit],
  );

  const setTab = useCallback(
    (next: PcmpMarketingTab) => {
      setSearch((prev) => ({
        ...prev,
        tab: next === PCMP_MARKETING_DEFAULT_TAB ? undefined : next,
        explained: undefined,
        page: undefined,
      }));
    },
    [setSearch],
  );

  const setExplained = useCallback(
    (next: boolean | undefined) => {
      setSearch((prev) => ({ ...prev, explained: next, page: undefined }));
    },
    [setSearch],
  );

  return {
    ...queue,
    tab,
    explained,
    queueParams,
    correctionParams,
    batchesParams,
    setTab,
    setExplained,
  };
}
